import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { fetchApiKeyStatus, generateApiKey, revokeApiKey } from '../api/apiKeys';
import { useAuth } from '../auth/AuthContext';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';

export function AccountPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [visibleApiKey, setVisibleApiKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const apiKeyQuery = useQuery({
    queryKey: ['me', 'api-key'],
    queryFn: fetchApiKeyStatus,
    enabled: auth.isAuthenticated,
    retry: false
  });

  const generateMutation = useMutation({
    mutationFn: generateApiKey,
    onSuccess: async (data) => {
      setVisibleApiKey(data.apiKey ?? null);
      setCopied(false);
      await queryClient.invalidateQueries({ queryKey: ['me', 'api-key'] });
    }
  });

  const revokeMutation = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: async () => {
      setVisibleApiKey(null);
      setCopied(false);
      await queryClient.invalidateQueries({ queryKey: ['me', 'api-key'] });
    }
  });

  const isBusy = generateMutation.isPending || revokeMutation.isPending;
  const hasApiKey = Boolean(apiKeyQuery.data?.hasApiKey);
  const keyDisplay = visibleApiKey ?? apiKeyQuery.data?.maskedApiKey ?? 'No API key created yet';

  const helperText = useMemo(() => {
    if (visibleApiKey) {
      return 'Copy this key now and paste it into fzClient connection settings. For security, the full key will only be shown immediately after generation or rotation.';
    }

    if (hasApiKey) {
      return 'An API key exists for this account. Rotate it to reveal a new key, or revoke it to disable client uploads.';
    }

    return 'Generate an API key to enable authenticated uploads from fzClient.';
  }, [hasApiKey, visibleApiKey]);

  async function copyKey() {
    if (!visibleApiKey) return;
    await navigator.clipboard.writeText(visibleApiKey);
    setCopied(true);
  }

  if (auth.isLoading) {
    return <LoadingState label="Checking session..." />;
  }

  if (!auth.isAuthenticated || !auth.user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="content-wrapper py-4">
      <div className="d-flex flex-column gap-1 mb-4">
        <p className="text-uppercase text-body-secondary small fw-semibold mb-0">Account</p>
        <h1 className="display-6 mb-0">Client API Key</h1>
        <p className="text-body-secondary mb-0">Manage the key used by fzClient when Upload Live Logs is enabled.</p>
      </div>

      {apiKeyQuery.isLoading ? <LoadingState label="Loading API key status..." /> : null}
      {apiKeyQuery.error ? <ErrorState error={apiKeyQuery.error} /> : null}
      {generateMutation.error ? <ErrorState error={generateMutation.error} /> : null}
      {revokeMutation.error ? <ErrorState error={revokeMutation.error} /> : null}

      <div className="card bg-body-tertiary border-secondary-subtle shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-column gap-3">
            <div>
              <label className="form-label fw-semibold" htmlFor="clientApiKey">API key</label>
              <div className="input-group">
                <input
                  id="clientApiKey"
                  className="form-control font-monospace"
                  type="text"
                  readOnly
                  value={keyDisplay}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => void copyKey()}
                  disabled={!visibleApiKey}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="form-text">{helperText}</div>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-primary"
                type="button"
                disabled={isBusy}
                onClick={() => void generateMutation.mutateAsync()}
              >
                {hasApiKey ? 'Rotate API Key' : 'Generate API Key'}
              </button>
              <button
                className="btn btn-outline-danger"
                type="button"
                disabled={isBusy || !hasApiKey}
                onClick={() => void revokeMutation.mutateAsync()}
              >
                Revoke API Key
              </button>
            </div>

            <div className="alert alert-info mb-0">
              Authenticated client uploads are public ranking submissions. To parse locally without uploading, turn off Upload Live Logs in fzClient.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
